import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarCheck, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { ar } from "date-fns/locale";

interface Props {
  childId: string;
  nurseryId: string;
}

const statusConfig: Record<string, { label: string; icon: any; color: string; bgColor: string }> = {
  present: { label: "حاضر", icon: CheckCircle2, color: "text-emerald-600", bgColor: "bg-emerald-100" },
  absent: { label: "غائب", icon: XCircle, color: "text-red-600", bgColor: "bg-red-100" },
  late: { label: "متأخر", icon: Clock, color: "text-amber-600", bgColor: "bg-amber-100" },
  excused: { label: "إجازة", icon: CalendarCheck, color: "text-blue-600", bgColor: "bg-blue-100" },
};

export default function ParentAttendance({ childId, nurseryId }: Props) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttendance();
  }, [childId]);

  const fetchAttendance = async () => {
    setLoading(true);
    const now = new Date();
    const start = format(startOfMonth(now), "yyyy-MM-dd");
    const end = format(endOfMonth(now), "yyyy-MM-dd");

    const { data } = await supabase
      .from("attendance")
      .select("*")
      .eq("attendee_id", childId)
      .eq("attendee_type", "child")
      .eq("nursery_id", nurseryId)
      .gte("date", start)
      .lte("date", end)
      .order("date", { ascending: false });

    setRecords(data || []);
    setLoading(false);
  };

  const presentCount = records.filter((r) => r.status === "present").length;
  const absentCount = records.filter((r) => r.status === "absent").length;
  const lateCount = records.filter((r) => r.status === "late").length;
  const attendanceRate = records.length > 0
    ? Math.round(((presentCount + lateCount) / records.length) * 100)
    : 0;

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "نسبة الحضور", value: `${attendanceRate}%`, color: "text-primary" },
          { label: "حاضر", value: presentCount, color: "text-emerald-600" },
          { label: "غائب", value: absentCount, color: "text-red-600" },
          { label: "متأخر", value: lateCount, color: "text-amber-600" },
        ].map((s, i) => (
          <Card key={i} className="shadow-card">
            <CardContent className="p-3 text-center">
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Records */}
      <Card className="shadow-card">
        <CardContent className="p-0">
          {records.length === 0 ? (
            <div className="py-12 text-center">
              <CalendarCheck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">لا توجد سجلات حضور لهذا الشهر</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[350px]">
              <div className="divide-y">
                {records.map((r) => {
                  const cfg = statusConfig[r.status] || statusConfig.present;
                  const Icon = cfg.icon;
                  return (
                    <div key={r.id} className="p-3 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cfg.bgColor}`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {format(new Date(r.date), "EEEE dd MMMM", { locale: ar })}
                        </p>
                      </div>
                      <Badge variant="outline" className={`text-xs ${cfg.color}`}>
                        {cfg.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
